import type { CreateImageRequest, Image, ImageFilters } from '@billx/types';

import { userApiClient } from '../clients';
import type { NucleusServiceResponse } from './types';
import { handleAxiosError } from './utils';

export class ImageService {
  /**
   * Upload single image to Firebase Storage
   */
  static async uploadImage(): Promise<
    NucleusServiceResponse<{ downloadURL: string; fileSize: number }>
  > {
    return {
      success: true,
      data: {
        downloadURL: '',
        fileSize: 20,
      },
    };
  }

  /**
   * Create image document
   */
  static async createImage(
    imageData: CreateImageRequest
  ): Promise<NucleusServiceResponse<Image>> {
    try {
      const response = await userApiClient.post('/media/images', imageData);

      return response.data;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  /**
   * Get images by entity with filters
   */
  static async getImagesByBusinessId(
    filters: ImageFilters
  ): Promise<NucleusServiceResponse<Image[]>> {
    try {
      const params = new URLSearchParams();
      if (filters.businessId) params.append('businessId', filters.businessId);
      if (filters.usecase) params.append('usecase', filters.usecase);
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await userApiClient.get('/media/images', { params });

      return response.data;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  /**
   * Delete image and its storage file
   */
  static async deleteImage(
    imageId: string
  ): Promise<NucleusServiceResponse<void>> {
    try {
      // Delete document
      const response = await userApiClient.delete(`/media/images/${imageId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  /**
   * Bulk upload images
   */
  static async bulkUploadImages(
    files: File[],
    businessId: string,
    entity: string = 'business',
    usecase: string = 'menu'
  ): Promise<NucleusServiceResponse<Image[]>> {
    try {
      const uploadedImages: Image[] = [];

      for (const file of files) {
        // Generate unique image ID
        const imageId = `image_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Create image document
        const fileExtension = file.name.split('.').pop() || 'jpg';
        const fileName = `${imageId}.${fileExtension}`;

        const imageData: CreateImageRequest = {
          businessId,
          usecase,
          originalName: file.name,
          fileName,
          filePath: `${entity}/${businessId}/${usecase}/${fileName}`,
          fileSize: 20,
          mimeType: file.type,
          width: 20,
          height: 20,
        };

        const createResult = await this.createImage(imageData);
        if (createResult.success && createResult.data) {
          uploadedImages.push(createResult.data);
        }
      }

      return { success: true, data: uploadedImages };
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  /**
   * Increment image usage count
   */
  static async incrementImageUsage(
    imageId: string
  ): Promise<NucleusServiceResponse<void>> {
    try {
      const response = await userApiClient.patch(
        `/media/images/${imageId}/usage`,
        {
          action: 'increment',
        }
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  /**
   * Decrement image usage count
   */
  static async decrementImageUsage(
    imageId: string
  ): Promise<NucleusServiceResponse<void>> {
    try {
      const response = await userApiClient.patch(
        `/media/images/${imageId}/usage`,
        {
          action: 'decrement',
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error decrementing image usage:', error);
      return handleAxiosError(error);
    }
  }

  /**
   * Get storage usage for an entity
   */
  static async getEntityStorageUsage(
    businessId: string,
    usecase?: string
  ): Promise<
    NucleusServiceResponse<{
      totalImages: number;
      totalSize: number;
      averageSize: number;
      usageByImage: Array<{
        imageId: string;
        fileName: string;
        fileSize: number;
        usageCount: number;
      }>;
    }>
  > {
    try {
      const imagesResult = await ImageService.getImagesByBusinessId({
        businessId,
        usecase,
      });
      if (!imagesResult.success) {
        return imagesResult;
      }

      const images = imagesResult.data;
      const totalSize = images.reduce((sum, image) => sum + image.fileSize, 0);
      const averageSize = images.length > 0 ? totalSize / images.length : 0;

      const usageByImage = images.map((image) => ({
        imageId: image.id,
        fileName: image.fileName,
        fileSize: image.fileSize,
        usageCount: image.usageCount,
      }));

      return {
        success: true,
        data: {
          totalImages: images.length,
          totalSize,
          averageSize,
          usageByImage,
        },
      };
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  /**
   * Get download URL for an image by its file path
   */
  static async getImageDownloadUrl(): Promise<NucleusServiceResponse<string>> {
    try {
      return {
        success: true,
        data: '',
      };
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  /**
   * Clean up unused images (images with usageCount = 0)
   */
  static async cleanupUnusedImages(
    businessId: string,
    usecase?: string
  ): Promise<
    NucleusServiceResponse<{
      deletedCount: number;
      freedSpace: number;
    }>
  > {
    try {
      const imagesResult = await ImageService.getImagesByBusinessId({
        businessId,
        usecase,
      });
      if (!imagesResult.success) {
        return imagesResult;
      }

      const unusedImages = imagesResult.data.filter(
        (image) => image.usageCount === 0
      );
      let deletedCount = 0;
      let freedSpace = 0;

      for (const image of unusedImages) {
        const deleteResult = await ImageService.deleteImage(image.id);
        if (deleteResult.success) {
          deletedCount++;
          freedSpace += image.fileSize;
        }
      }

      return {
        success: true,
        data: {
          deletedCount,
          freedSpace,
        },
      };
    } catch (error) {
      console.error('Error cleaning up unused images:', error);
      return handleAxiosError(error);
    }
  }
  /**
   * Get upload URL for direct S3 upload
   */
  static async getUploadUrl(data: {
    businessId: string;
    usecase: string;
    originalName: string;
    mimeType: string;
    fileSize: number;
  }): Promise<
    NucleusServiceResponse<{
      uploadUrl: string;
      publicUrl: string;
      filePath: string;
      imageId: string;
    }>
  > {
    try {
      const response = await userApiClient.post('/media/upload-url', data);
      return response.data;
    } catch (error) {
      return handleAxiosError(error);
    }
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  static async uploadToS3(
    uploadUrl: string,
    file: Blob | File,
    mimeType: string
  ): Promise<boolean> {
    try {
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': mimeType,
        },
      });
      return true;
    } catch (error) {
      console.error('Error uploading to S3:', error);
      return false;
    }
  }
}
